import { useCallback, useState } from "react";

/**
 * El techo de filas del sistema, en un solo lugar.
 *
 * No es un numero elegido acá: `consultar()` (backend, `db.ts`) recorta todo a
 * 500 y el esquema `paginacion` de las rutas rechaza pedir más. Pedir 800 no
 * devuelve 800, devuelve 500 sin avisar, así que la pantalla tiene que saber
 * dónde está la pared para dejar de ofrecer «Ver más» y decir la verdad.
 *
 * Si alguna vez se mueve el tope del backend, se mueve acá en el mismo commit.
 */
export const TOPE_FILAS = 500;

export interface VerMas {
  /** Cuantas filas pedir ahora. Va derecho al `limite` de la consulta. */
  limite: number;
  /**
   * Pide una pagina mas. Es `undefined` cuando ya se llego a `TOPE_FILAS`: un
   * boton que no puede traer nada nuevo es peor que no tener boton.
   */
  verMas: (() => void) | undefined;
  /** Vuelve a la primera pagina. Se llama al cambiar un filtro. */
  reiniciar: () => void;
}

/**
 * La ventana de filas de un listado, que crece con «Ver más».
 *
 * Crece el `limite` desde cero en vez de pegar páginas por `offset`: dos
 * páginas traídas en momentos distintos pueden solaparse o saltearse filas si
 * entre medio se cargó una venta, y una lista con un renglón repetido es peor
 * que una corta. Es el mismo criterio que ya usaba `MovimientosStock`.
 *
 * Al cambiar los filtros hay que llamar a `reiniciar()`: quedarse en la página
 * 3 de la búsqueda anterior muestra un recorte que nadie pidió.
 */
export function useVerMas(porPagina: number): VerMas {
  const [paginas, setPaginas] = useState(1);

  const limite = Math.min(porPagina * paginas, TOPE_FILAS);
  const reiniciar = useCallback(() => setPaginas(1), []);
  const verMas = useCallback(() => setPaginas((p) => p + 1), []);

  return {
    limite,
    verMas: limite < TOPE_FILAS ? verMas : undefined,
    reiniciar,
  };
}
